import { addDays } from '@asas/domain/calendar';
import { isEmployedOn, isPlannable, weeklyHoursOn } from '@asas/domain/capacity';
import { assignedFachgruppeOn, membershipOn } from '@asas/domain/membership';
import type {
  EmployeeCompleteInput,
  EmployeeDetails,
  EmployeeInput,
  EmployeeOptions,
  EmployeeUpdateInput,
  IsoDate,
} from '@asas/domain';

import type { FeldBefund } from '../daten/fehler';

/**
 * Das Mitarbeiterformular als reiner Zustand (AC-103) — ohne React, ohne Netz,
 * damit die Umwandlung zwischen Eingabefeldern und API-Eingabe ohne Browser
 * pruefbar ist.
 *
 * Die Felder sind Zeichenketten, so wie sie aus `<input>` kommen; `''` heisst
 * „leer". Die fachlichen Regeln stehen NICHT hier, sondern im Domaenenvertrag
 * und am Server: die Oberflaeche schickt, was eingegeben wurde, und zeigt die
 * Feldbefunde der Antwort an. Eine zweite Pruefung hier liefe auseinander.
 */
export interface FormularStand {
  /**
   * Wofuer das Formular gilt: `neu`, `bearbeiten` oder `vervollstaendigen`. Kein
   * Eingabefeld — es haelt fest, wie die uebrigen Felder zu lesen sind:
   * `companyId` ist beim Vervollstaendigen die Gesellschaft ab dem Eintritt,
   * beim Bearbeiten ein Wechsel. Eingaben aus dem einen Modus duerfen nie in den
   * anderen uebertragen werden (`aufNeuenStand`).
   */
  readonly modus: string;
  readonly name: string;
  /**
   * Beim Anlegen und Vervollstaendigen die Gesellschaft ab dem Eintritt
   * (Pflicht). Beim Bearbeiten die NEUE Gesellschaft eines Wechsels; `''` =
   * kein Wechsel (AC-103, Ergaenzung: eine Historie wie die Fachgruppe).
   */
  readonly companyId: string;
  /** Gueltig ab fuer einen Gesellschaftswechsel (nur beim Bearbeiten). */
  readonly companyAb: string;
  /** Beim Vervollstaendigen: `intern` oder `extern`. Sonst ungenutzt. */
  readonly personType: string;
  /**
   * Beim Anlegen die Fachgruppe ab dem Eintritt (Pflicht, AK-2 von AC-104).
   * Beim Bearbeiten die NEUE Fachgruppe eines Wechsels; `''` = kein Wechsel.
   * Ein Wechsel ist ein Eintrag „ab Datum X", kein Ueberschreiben.
   */
  readonly fachgruppeId: string;
  /** Gueltig ab fuer einen Fachgruppenwechsel (nur beim Bearbeiten). */
  readonly fachgruppeAb: string;
  /** `''` = keine Rolle. */
  readonly roleId: string;
  readonly startsOn: string;
  /** `''` = kein Austritt. */
  readonly endsOn: string;
  /** Neue Soll-Wochenstunden; beim Bearbeiten `''` = unveraendert. */
  readonly weeklyHours: string;
  /** Gueltig ab fuer `weeklyHours`. */
  readonly effectiveFrom: string;
}

export type FormularFeld = keyof FormularStand;

export const FORMULAR_FELDER: readonly FormularFeld[] = [
  'modus',
  'name',
  'companyId',
  'companyAb',
  'personType',
  'fachgruppeId',
  'fachgruppeAb',
  'roleId',
  'startsOn',
  'endsOn',
  'weeklyHours',
  'effectiveFrom',
];

export function leeresFormular(): FormularStand {
  return {
    modus: 'neu',
    name: '',
    companyId: '',
    companyAb: '',
    personType: '',
    fachgruppeId: '',
    fachgruppeAb: '',
    roleId: '',
    startsOn: '',
    endsOn: '',
    weeklyHours: '',
    effectiveFrom: '',
  };
}

/**
 * Der vorgeschlagene Tag fuer einen Fachgruppenwechsel (AC-104): der Montag
 * NACH der laufenden Woche. Der Server liesse schon den laufenden Montag zu —
 * dann wechselte aber die ganze laufende Woche, samt der Tage, die schon
 * gearbeitet und vielleicht gebucht sind, und die alte Fachgruppenleitung
 * verloere die Person mitten in ihrer Wochenplanung. Das soll eine bewusste
 * Wahl sein, keine Vorgabe.
 *
 * Tritt die Person erst danach ein, ist der Vorschlag ihre erste Zuordnung (ab
 * dem Eintritt): wer vor dem Eintritt die Fachgruppe aendert, meint die, in der
 * sie anfaengt.
 */
export function fachgruppenwechselVorschlag(
  employee: EmployeeDetails,
  changesFrom: IsoDate,
): IsoDate {
  const naechsterMontag = addDays(changesFrom, 7);

  // Die erste Zuordnung haengt am Eintritt (`planEmployeeUpdate`); ein Eintrag
  // genau dort ersetzt sie, solange er noch nicht wirkt.
  return employee.startsOn !== null && employee.startsOn > naechsterMontag
    ? employee.startsOn
    : naechsterMontag;
}

/**
 * Das Formular zum Bearbeiten: Stammdaten aus dem gespeicherten Stand, die
 * Soll-Wochenstunden LEER — eine Aenderung ist ein neuer Eintrag „ab Datum X",
 * kein Ueberschreiben des alten (AK-3). Vorbelegt ist der fruehestmoegliche
 * Tag, den der Server nennt. Ebenso die Fachgruppe (AC-104): LEER heisst „kein
 * Wechsel", die aktuelle zeigt die Ansicht daneben.
 */
export function formularAus(employee: EmployeeDetails, changesFrom: IsoDate): FormularStand {
  const wechselAb = fachgruppenwechselVorschlag(employee, changesFrom);

  return {
    modus: 'bearbeiten',
    name: employee.name,
    // Wie die Fachgruppe: LEER heisst „kein Wechsel"; dieselbe Regel, derselbe Vorschlag.
    companyId: '',
    companyAb: wechselAb,
    personType: employee.personType ?? '',
    fachgruppeId: '',
    fachgruppeAb: wechselAb,
    roleId: employee.roleId ?? '',
    startsOn: employee.startsOn ?? '',
    endsOn: employee.endsOn ?? '',
    weeklyHours: '',
    effectiveFrom: changesFrom,
  };
}

/**
 * Das Formular zum Vervollstaendigen (AC-103, Ergaenzung): ein Stammsatz aus
 * PACS oder eine externe Person. Vorbelegt ist der gespeicherte Typ — ohne Typ
 * „intern", der haeufige Fall — und die gespeicherte Rolle, damit sie beim
 * Speichern nicht verloren geht. Die Pflichtangaben sind leer wie beim Anlegen.
 */
export function formularZumVervollstaendigen(employee: EmployeeDetails): FormularStand {
  return {
    ...leeresFormular(),
    modus: 'vervollstaendigen',
    name: employee.name,
    personType: employee.personType ?? 'intern',
    roleId: employee.roleId ?? '',
  };
}

/**
 * Traegt die Eingaben einer Person auf einen NEUEN gespeicherten Stand ueber.
 *
 * `basis` ist der Stand, von dem aus sie getippt hat. Was sie gegenueber
 * `basis` geaendert hat, bleibt; jedes andere Feld zeigt danach `neu`. Ohne
 * diesen Schritt hielte das Formular nach „Neu laden" (etwa nach einem
 * Konflikt) oder nach einer Neuanmeldung die ALTEN Werte unberuehrter Felder —
 * und `aenderungEingabe` schickte sie als Aenderung los: Die Aenderung einer
 * anderen Person waere still zurueckgedreht, mit gueltiger Version.
 */
export function aufNeuenStand(
  stand: FormularStand,
  basis: FormularStand,
  neu: FormularStand,
): FormularStand {
  // Anderer Modus (etwa: inzwischen vervollstaendigt): dieselben Felder
  // bedeuten etwas anderes; uebertragen hiesse, Fremdwerte still zu
  // ueberschreiben. Dann gilt der neue Stand (`modusGewechselt`).
  if (modusGewechselt(basis, neu)) return neu;

  const ergebnis: { -readonly [K in FormularFeld]: string } = { ...neu };

  for (const feld of FORMULAR_FELDER) {
    if (stand[feld] !== basis[feld]) ergebnis[feld] = stand[feld];
  }

  return ergebnis;
}

/**
 * Gilt der neue Stand fuer einen anderen Modus als der, von dem aus getippt
 * wurde? Dann verwirft `aufNeuenStand` die Eingaben; die Oberflaeche sagt das.
 */
export function modusGewechselt(basis: FormularStand, neu: FormularStand): boolean {
  return basis.modus !== neu.modus;
}

/** Hat die Person gegenueber dem Stand, von dem aus sie getippt hat, etwas geaendert? */
export function hatEigeneEingaben(stand: FormularStand, basis: FormularStand): boolean {
  return FORMULAR_FELDER.some((feld) => stand[feld] !== basis[feld]);
}

/**
 * Die Felder mit gespeichertem Wert. Die Soll-Angabe im Formular ist eine neue
 * Eingabe und kann nicht „inzwischen anders gespeichert" sein.
 */
const GESPEICHERTE_FELDER: readonly FormularFeld[] = [
  'name',
  'personType',
  'companyId',
  'fachgruppeId',
  'roleId',
  'startsOn',
  'endsOn',
];

/**
 * Die Felder, die die Person geaendert hat UND die inzwischen anders
 * gespeichert sind. Dort behaelt `aufNeuenStand` ihre Eingabe — ohne Hinweis
 * ersetzte Speichern den neuen Wert, ohne dass sie ihn je gesehen hat.
 */
export function konfliktFelder(
  stand: FormularStand,
  basis: FormularStand,
  neu: FormularStand,
): FormularFeld[] {
  return GESPEICHERTE_FELDER.filter(
    (feld) => stand[feld] !== basis[feld] && neu[feld] !== basis[feld] && neu[feld] !== stand[feld],
  );
}

/** Je Konfliktfeld der Hinweis, was inzwischen gespeichert ist — in Anzeigeform. */
export function konfliktHinweise(
  stand: FormularStand,
  basis: FormularStand,
  neu: FormularStand,
  optionen: EmployeeOptions,
): Partial<Record<FormularFeld, string>> {
  const namen: Partial<Record<FormularFeld, ReadonlyMap<string, string>>> = {
    companyId: new Map(optionen.companies.map((eintrag) => [eintrag.id, eintrag.name])),
    fachgruppeId: new Map(optionen.fachgruppen.map((eintrag) => [eintrag.id, eintrag.name])),
    roleId: new Map(optionen.roles.map((eintrag) => [eintrag.id, eintrag.name])),
  };
  const hinweise: Partial<Record<FormularFeld, string>> = {};

  if (modusGewechselt(basis, neu)) return hinweise;

  for (const feld of konfliktFelder(stand, basis, neu)) {
    const wert = neu[feld];
    const anzeige =
      wert === ''
        ? 'leer'
        : feld === 'startsOn' || feld === 'endsOn'
          ? datumAnzeige(wert)
          : (namen[feld]?.get(wert) ?? wert);

    hinweise[feld] =
      `Inzwischen anders gespeichert: ${anzeige}. Speichern ersetzt das durch Ihre Eingabe.`;
  }

  return hinweise;
}

/** Prueft einen Formularstand aus dem Browserspeicher — veraenderbarer Text, kein Vertrauen (AC-016, Entscheidung 6). */
export function pruefeFormularStand(roh: unknown): FormularStand | null {
  if (typeof roh !== 'object' || roh === null) return null;

  const werte = roh as Record<string, unknown>;
  const stand: Partial<Record<FormularFeld, string>> = {};

  for (const feld of FORMULAR_FELDER) {
    const wert = werte[feld];

    if (typeof wert !== 'string') return null;
    stand[feld] = wert;
  }

  return stand as FormularStand;
}

/**
 * „38,5" und „38.5" meinen dasselbe. Auf der Leitung steht der Punkt
 * (README, „Drahtformat"); das Komma ist die deutsche Schreibweise im Feld.
 * Sonst wird nichts umgedeutet — was keine Zahl ist, beurteilt der Server.
 */
export function stundenEingabe(value: string): string {
  return value.trim().replace(',', '.');
}

/**
 * Die Eingabe fuer `employees.create`. Leere Pflichtfelder gehen leer hinaus,
 * damit der Server sie beim Feld benennt, statt dass die Oberflaeche eine
 * eigene Meldung erfindet.
 *
 * Die ersten Soll-Wochenstunden gelten ab dem Eintritt (Domaenenvertrag):
 * `effectiveFrom` ist beim Anlegen deshalb kein eigenes Feld.
 */
export function anlegenEingabe(stand: FormularStand): EmployeeInput {
  return {
    name: stand.name,
    companyId: stand.companyId,
    fachgruppeId: stand.fachgruppeId,
    roleId: stand.roleId === '' ? null : stand.roleId,
    startsOn: stand.startsOn,
    endsOn: stand.endsOn === '' ? null : stand.endsOn,
    weeklyHours: { effectiveFrom: stand.startsOn, hours: stundenEingabe(stand.weeklyHours) },
  };
}

/**
 * Die Eingabe fuer `employees.update`: NUR die Felder, die sich gegenueber dem
 * gespeicherten Stand aendern (teilweise Aenderung, AC-101). `null`, wenn
 * nichts zu senden ist.
 */
export function aenderungEingabe(
  employee: EmployeeDetails,
  stand: FormularStand,
): EmployeeUpdateInput | null {
  const aenderung: { -readonly [K in keyof EmployeeUpdateInput]?: EmployeeUpdateInput[K] } = {};

  // Haengt ein PACS-Kontakt am Satz, kommt der Name aus PACS; das gesperrte Feld
  // geht nie hinaus (Befund aus dem KI-Agent-Review).
  if (employee.pacsId === null && stand.name !== employee.name) aenderung.name = stand.name;
  if (stand.roleId !== (employee.roleId ?? '')) {
    aenderung.roleId = stand.roleId === '' ? null : stand.roleId;
  }
  if (stand.startsOn !== (employee.startsOn ?? '')) aenderung.startsOn = stand.startsOn;
  if (stand.endsOn !== (employee.endsOn ?? '')) {
    aenderung.endsOn = stand.endsOn === '' ? null : stand.endsOn;
  }
  if (stand.weeklyHours.trim() !== '') {
    aenderung.weeklyHours = {
      effectiveFrom: stand.effectiveFrom,
      hours: stundenEingabe(stand.weeklyHours),
    };
  }
  // AC-104: ein Wechsel ist ein Eintrag „ab Datum X". Ob der Tag zulaessig ist
  // (Montag, nicht in einer abgeschlossenen Woche), sagt der Server am Feld.
  if (stand.fachgruppeId !== '') {
    aenderung.fachgruppe = { effectiveFrom: stand.fachgruppeAb, fachgruppeId: stand.fachgruppeId };
  }
  // AC-103, Ergaenzung: dasselbe fuer die Gesellschaft.
  if (stand.companyId !== '') {
    aenderung.company = { effectiveFrom: stand.companyAb, companyId: stand.companyId };
  }

  if (Object.keys(aenderung).length === 0) {
    return null;
  }

  return { ...aenderung, id: employee.id, expectedVersion: employee.version };
}

/**
 * Die Eingabe fuer `employees.complete` (AC-103, Ergaenzung). Bei „intern"
 * dieselben Pflichtangaben wie beim Anlegen, leer hinaus, damit der Server sie
 * beim Feld benennt.
 */
export function vervollstaendigenEingabe(
  employee: EmployeeDetails,
  stand: FormularStand,
): EmployeeCompleteInput {
  const kopf = { id: employee.id, expectedVersion: employee.version };

  if (stand.personType === 'extern') return { ...kopf, personType: 'extern' };

  const angaben = anlegenEingabe(stand);

  return {
    ...kopf,
    personType: 'intern',
    companyId: angaben.companyId,
    fachgruppeId: angaben.fachgruppeId,
    roleId: angaben.roleId ?? null,
    startsOn: angaben.startsOn,
    endsOn: angaben.endsOn ?? null,
    weeklyHours: angaben.weeklyHours,
  };
}

/** Einen Soll-Eintrag ab der Grenze `changesFrom` entfernen; ob das zulaessig ist, entscheidet der Server. */
export function entfernenEingabe(
  employee: EmployeeDetails,
  effectiveFrom: IsoDate,
): EmployeeUpdateInput {
  return {
    id: employee.id,
    expectedVersion: employee.version,
    weeklyHours: { effectiveFrom, hours: null },
  };
}

/** Einen geplanten, noch nicht wirksamen Fachgruppenwechsel entfernen (AC-104). */
export function fachgruppenwechselEntfernenEingabe(
  employee: EmployeeDetails,
  effectiveFrom: IsoDate,
): EmployeeUpdateInput {
  return {
    id: employee.id,
    expectedVersion: employee.version,
    fachgruppe: { effectiveFrom, fachgruppeId: null },
  };
}

/** Einen geplanten, noch nicht wirksamen Gesellschaftswechsel entfernen (AC-103, Ergaenzung). */
export function gesellschaftswechselEntfernenEingabe(
  employee: EmployeeDetails,
  effectiveFrom: IsoDate,
): EmployeeUpdateInput {
  return {
    id: employee.id,
    expectedVersion: employee.version,
    company: { effectiveFrom, companyId: null },
  };
}

/** Welches Formularfeld ein Feldbefund der API meint (`data.validation[].path`). */
const FELD_NACH_PFAD: Readonly<Record<string, FormularFeld>> = {
  name: 'name',
  personType: 'personType',
  companyId: 'companyId',
  'company.companyId': 'companyId',
  'company.effectiveFrom': 'companyAb',
  fachgruppeId: 'fachgruppeId',
  'fachgruppe.fachgruppeId': 'fachgruppeId',
  'fachgruppe.effectiveFrom': 'fachgruppeAb',
  roleId: 'roleId',
  startsOn: 'startsOn',
  endsOn: 'endsOn',
  'weeklyHours.hours': 'weeklyHours',
  'weeklyHours.effectiveFrom': 'effectiveFrom',
};

/**
 * Ordnet die Feldbefunde den Formularfeldern zu. Ein Befund ohne passendes
 * Feld geht nicht verloren, sondern landet unter `allgemein`.
 */
export function feldMeldungen(felder: readonly FeldBefund[]): {
  readonly jeFeld: Partial<Record<FormularFeld, string>>;
  readonly allgemein: readonly string[];
} {
  const jeFeld: Partial<Record<FormularFeld, string>> = {};
  const allgemein: string[] = [];

  for (const befund of felder) {
    const feld = Object.hasOwn(FELD_NACH_PFAD, befund.path)
      ? FELD_NACH_PFAD[befund.path]
      : undefined;

    if (feld === undefined) {
      allgemein.push(befund.message);
    } else {
      jeFeld[feld] ??= befund.message;
    }
  }

  return { jeFeld, allgemein };
}

/** `2026-10-01` -> `01.10.2026`. Nur Anzeige; gerechnet wird mit dem ISO-Datum. */
export function datumAnzeige(date: IsoDate | null): string {
  if (date === null || date === '') return '—';

  const [jahr, monat, tag] = date.split('-');

  return `${tag ?? ''}.${monat ?? ''}.${jahr ?? ''}`;
}

/** `38.50` -> `38,50 h`. */
export function stundenAnzeige(hours: string | null): string {
  return hours === null ? '—' : `${hours.replace('.', ',')} h`;
}

/**
 * Wird der Satz vervollstaendigt statt bearbeitet? Ja fuer jede Person, die
 * nicht planbar ist — ein Stammsatz aus PACS oder eine externe Person
 * (AC-103, Ergaenzung). Gerechnet in `@asas/domain/capacity`, nicht daneben.
 */
export function istZuVervollstaendigen(employee: EmployeeDetails): boolean {
  return !isPlannable(employee);
}

/** „Intern", „Extern" oder „—" fuer einen Satz aus PACS, dessen Typ noch offen ist. */
export function typAnzeige(employee: EmployeeDetails): string {
  return employee.personType === 'intern'
    ? 'Intern'
    : employee.personType === 'extern'
      ? 'Extern'
      : '—';
}

/** Die Stammsaetze aus PACS, mit denen sich ein gepflegter Satz zusammenfuehren laesst. */
export function stammsaetzeAusPacs(
  mitarbeiter: readonly EmployeeDetails[],
): (EmployeeDetails & { readonly pacsId: number })[] {
  return mitarbeiter.filter(
    (employee): employee is EmployeeDetails & { readonly pacsId: number } =>
      employee.recordStatus === 'zu_vervollstaendigen' && employee.pacsId !== null,
  );
}

export type Beschaeftigung = 'kuenftig' | 'beschaeftigt' | 'ausgetreten';

/** Wie die Person heute zur Beschaeftigung steht — fuer die Liste. */
export function beschaeftigungAm(employee: EmployeeDetails, heute: IsoDate): Beschaeftigung {
  if (isEmployedOn(employee, heute)) return 'beschaeftigt';

  return employee.startsOn !== null && employee.startsOn > heute ? 'kuenftig' : 'ausgetreten';
}

export const BESCHAEFTIGUNG_LABELS: Readonly<Record<Beschaeftigung, string>> = {
  kuenftig: 'Eintritt geplant',
  beschaeftigt: 'Beschäftigt',
  ausgetreten: 'Ausgetreten',
};

/** Die Soll-Wochenstunden, die heute gelten — `null` ohne Eintrag oder ausserhalb der Beschaeftigung. */
export function sollHeute(employee: EmployeeDetails, heute: IsoDate): string | null {
  return isEmployedOn(employee, heute) ? weeklyHoursOn(employee.weeklyHours, heute) : null;
}

/** Ein Eintrag ab `changesFrom` ist noch aenderbar; davor liegt er in einer abgeschlossenen Woche. */
export function istAenderbar(effectiveFrom: IsoDate, changesFrom: IsoDate): boolean {
  return effectiveFrom >= changesFrom;
}

/**
 * Darf die Oberflaeche „Entfernen" anbieten? Nur fuer einen aenderbaren
 * Eintrag, der nicht der erste ist: ohne den ersten fehlte ab dem Eintritt das
 * Soll (Domaenenvertrag). Nur Anzeige — entscheiden tut der Server.
 */
export function istEntfernbar(
  eintraege: readonly { readonly effectiveFrom: IsoDate }[],
  effectiveFrom: IsoDate,
  changesFrom: IsoDate,
): boolean {
  return (
    istAenderbar(effectiveFrom, changesFrom) &&
    eintraege.some((eintrag) => eintrag.effectiveFrom < effectiveFrom)
  );
}

/**
 * Der naechste geplante Fachgruppenwechsel nach `heute` — in eine ANDERE
 * Fachgruppe als die, die vorher gilt. `null`, wenn keiner geplant ist. Nur
 * Anzeige; was gilt, rechnet `@asas/domain/membership`.
 */
export function naechsterFachgruppenwechsel(
  employee: EmployeeDetails,
  heute: IsoDate,
): { readonly effectiveFrom: IsoDate; readonly fachgruppeId: string } | null {
  // Aus `membership.ts`, nicht daneben gerechnet: ein Wechsel nach dem Austritt
  // oder vor dem Eintritt wirkt nie und erscheint deshalb auch nicht.
  const until = membershipOn(employee, heute)?.until ?? null;

  if (until === null) return null;

  const effectiveFrom = addDays(until, 1);
  const fachgruppeId = assignedFachgruppeOn(employee, effectiveFrom);

  return fachgruppeId === null ? null : { effectiveFrom, fachgruppeId };
}
